import React, { useEffect, useState } from "react";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";

interface Category {
  id: string;
  name: string;
  description: string;
  ListTypes: ListType[];
  legacyListsCount: number;
}

interface ListType {
  id: string;
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  userListsCount: number;
}

interface CreateListTypeForm {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
}

const ListTypesAdmin = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateListTypeForm>({
    categoryId: "",
    name: "",
    slug: "",
    description: "",
    isActive: true,
  });

  useEffect(() => {
    fetchListTypes();
  }, []);

  const fetchListTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/admin/list-types", {
        method: "GET",
        ...getBearerHeaders(),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch list types");
      }

      const data = await response.json();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleNameChange = (name: string) => {
    setCreateForm((prev) => ({
      ...prev,
      name,
      slug: generateSlug(name),
    }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch("/api/admin/list-types", {
        method: "POST",
        ...getBearerHeaders(),
        body: JSON.stringify(createForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create list type");
      }

      // Reset form and refresh data
      setCreateForm({
        categoryId: "",
        name: "",
        slug: "",
        description: "",
        isActive: true,
      });
      setShowCreateForm(false);
      await fetchListTypes();

      alert(data.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleToggleActive = async (listType: ListType) => {
    try {
      const response = await fetch(`/api/admin/list-types/${listType.id}`, {
        method: "PUT",
        ...getBearerHeaders(),
        body: JSON.stringify({
          isActive: !listType.isActive,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update list type");
      }

      await fetchListTypes();
      alert(data.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleMigrateLegacy = async (listType: ListType) => {
    if (
      !confirm(
        `Migrate all legacy lists to "${listType.name}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `/api/admin/list-types/${listType.id}/migrate-legacy`,
        {
          method: "POST",
          ...getBearerHeaders(),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to migrate legacy lists");
      }

      await fetchListTypes();
      alert(data.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async (listType: ListType, hard = false) => {
    const action = hard ? "permanently delete" : "deactivate";
    if (!confirm(`Are you sure you want to ${action} "${listType.name}"?`)) {
      return;
    }

    try {
      const url = hard
        ? `/api/admin/list-types/${listType.id}?hard=true`
        : `/api/admin/list-types/${listType.id}`;

      const response = await fetch(url, {
        method: "DELETE",
        ...getBearerHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete list type");
      }

      await fetchListTypes();
      alert(data.message);
    } catch (err) {
      alert(err instanceof Error ? err.message : "An error occurred");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-white">Loading list types...</div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-400 p-4">Error: {error}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">List Types Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          {showCreateForm ? "Cancel" : "Create New List Type"}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-gray-800 p-6 rounded-lg mb-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">
            Create New List Type
          </h3>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Category
              </label>
              <select
                value={createForm.categoryId}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    categoryId: e.target.value,
                  }))
                }
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                placeholder="e.g., Top 10 GOAT MCs"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Slug (URL-friendly)
              </label>
              <input
                type="text"
                value={createForm.slug}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, slug: e.target.value }))
                }
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2"
                placeholder="top-10-goat-mcs"
                pattern="^[a-z0-9-]+$"
                required
              />
              <p className="text-xs text-gray-400 mt-1">
                Only lowercase letters, numbers, and hyphens allowed
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={createForm.description}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 h-24"
                placeholder="Describe what this list type is about..."
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={createForm.isActive}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                className="mr-2"
              />
              <label htmlFor="isActive" className="text-sm text-gray-300">
                Active (users can create lists of this type)
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
              >
                Create List Type
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-6">
        {categories.map((category) => (
          <div
            key={category.id}
            className="bg-gray-800 rounded-lg border border-gray-700"
          >
            <div className="p-6 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    {category.name}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {category.description}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-400">
                    {category.ListTypes?.length || 0} List Types
                  </div>
                  {category.legacyListsCount > 0 && (
                    <div className="text-sm text-yellow-400">
                      {category.legacyListsCount} Legacy Lists
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6">
              {category.ListTypes && category.ListTypes.length > 0 ? (
                <div className="space-y-4">
                  {category.ListTypes.map((listType) => (
                    <div
                      key={listType.id}
                      className="bg-gray-700 p-4 rounded-lg"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-medium text-white">
                              {listType.name}
                            </h4>
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                listType.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {listType.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <p className="text-gray-300 text-sm mt-1">
                            Slug:{" "}
                            <code className="bg-gray-600 px-2 py-1 rounded text-xs">
                              {listType.slug}
                            </code>
                          </p>
                          {listType.description && (
                            <p className="text-gray-400 text-sm mt-2">
                              {listType.description}
                            </p>
                          )}
                          <div className="text-sm text-gray-400 mt-2">
                            {listType.userListsCount} user lists • Created{" "}
                            {new Date(listType.createdAt).toLocaleDateString()}
                          </div>
                        </div>

                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handleToggleActive(listType)}
                            className={`px-3 py-1 text-xs rounded ${
                              listType.isActive
                                ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                                : "bg-green-600 hover:bg-green-700 text-white"
                            }`}
                          >
                            {listType.isActive ? "Deactivate" : "Activate"}
                          </button>

                          {category.legacyListsCount > 0 && (
                            <button
                              onClick={() => handleMigrateLegacy(listType)}
                              className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                            >
                              Migrate Legacy
                            </button>
                          )}

                          <button
                            onClick={() => handleDelete(listType, false)}
                            className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">
                    No list types created for this category yet.
                  </p>
                  <button
                    onClick={() => {
                      setCreateForm((prev) => ({
                        ...prev,
                        categoryId: category.id,
                      }));
                      setShowCreateForm(true);
                    }}
                    className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    Create the first one →
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListTypesAdmin;
